/**
 * Formaty, w których człowiek czyta liczby i numery.
 *
 * Serwer przysyła kwoty jako napisy („3936.00”), daty jako ISO („2026-09-05”), a NIP i IBAN
 * bez separatorów — bo tak się je przechowuje i porównuje. Do oczu idzie co innego:
 * 3 936,00 zł, 05.09.2026, 521-301-72-28. Format liczby idzie za **językiem** odbiorcy,
 * waluta za **firmą**; dlatego wszystkie funkcje biorą `locale`, a nie zgadują go z przeglądarki.
 */
import type { Locale } from './i18n';

/** Kod języka w formie, którą rozumie `Intl` — nasze kody są z nią zgodne. */
const intlLocale: Record<Locale, string> = { pl: 'pl-PL', en: 'en-GB', uk: 'uk-UA', ru: 'ru-RU' };

/**
 * `useGrouping: 'always'` jest w standardzie od ES2023, ale typy TypeScriptu w wersji ES2022
 * znają jeszcze tylko wartość logiczną. Przeglądarki rozumieją napis od dawna, a `true` znaczy
 * „zastosuj regułę języka” — czyli dokładnie to, czego tu nie chcemy.
 */
const alwaysGrouped = { useGrouping: 'always' } as unknown as Intl.NumberFormatOptions;

/**
 * Kwota bez waluty — do tabel, gdzie waluta stoi w nagłówku kolumny.
 *
 * Zawsze dwie cyfry po przecinku: kolumna kwot ma się zgadzać pionowo, a „1 200” obok
 * „398,37” rozjeżdża wzrok. Wartości nieliczbowej nie udajemy — wraca taka, jaka przyszła.
 *
 * Grupowanie wymuszamy (`always`), bo polska reguła `Intl` zostawia cztery cyfry bez
 * separatora: „3936,00” stałoby w kolumnie tuż pod „20 500,00” i czytałoby się jako inny rząd
 * wielkości. W zdaniu to niuans, w księdze to pomyłka.
 */
export function amount(value: string | number | null | undefined, locale: Locale): string {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) return String(value);
  return new Intl.NumberFormat(intlLocale[locale], {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...alwaysGrouped,
  }).format(parsed);
}

/** Skrót waluty tak, jak pisze się ją w danym języku: po polsku „zł”, nie „PLN”. */
export function currencyLabel(currency: string, locale: Locale): string {
  return currency === 'PLN' && locale !== 'en' ? 'zł' : currency;
}

/** Kwota z walutą — do kafelków, podsumowań i zdań. */
export function money(
  value: string | number | null | undefined,
  locale: Locale,
  currency = 'PLN',
): string {
  const written = amount(value, locale);
  return written === '—' ? written : `${written} ${currencyLabel(currency, locale)}`;
}

/**
 * Data dzienna z ISO.
 *
 * Kropki zamiast myślników, bo tak zapisuje się datę na fakturze i w księdze;
 * po angielsku ukośniki, żeby nie sugerować amerykańskiej kolejności miesiąca i dnia.
 */
export function date(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return '—';
  const match = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return iso;
  const [, year, month, day] = match;
  return locale === 'en' ? `${day}/${month}/${year}` : `${day}.${month}.${year}`;
}

/** Miesiąc słownie: „sierpień 2026”. Okres przychodzi jako „2026-08”. */
export function period(value: string | null | undefined, locale: Locale): string {
  if (!value) return '—';
  const match = value.match(/^(\d{4})-(\d{2})$/);
  if (!match) return value;
  const [, year, month] = match;
  const written = new Intl.DateTimeFormat(intlLocale[locale], { month: 'long', year: 'numeric' }).format(
    new Date(Number(year), Number(month) - 1, 1),
  );
  return written;
}

/** Miesiąc cyframi: „08.2026”. Na kafelkach okresów, gdzie liczy się równa szerokość. */
export function periodShort(value: string): string {
  const match = value.match(/^(\d{4})-(\d{2})$/);
  return match ? `${match[2]}.${match[1]}` : value;
}

/** NIP czyta się w grupach; zapis w bazie zostaje bez separatorów. */
export function nip(value: string | null | undefined): string {
  if (!value) return '—';
  const digits = value.replace(/[^0-9]/g, '');
  return digits.length === 10
    ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`
    : value;
}

/** Numer rachunku w grupach po cztery — tak podaje go bank i tak przepisuje go człowiek. */
export function iban(value: string | null | undefined): string {
  if (!value) return '—';
  const compact = value.replace(/\s+/g, '').toUpperCase();
  return compact.replace(/(.{4})/g, '$1 ').trim();
}

/** Inicjały do znaczka przy nazwie — dwie litery, bez znaków spoza alfabetu. */
export function initials(name: string): string {
  const words = name.split(/[\s.,-]+/).filter((word) => /\p{L}/u.test(word));
  const letters = words.slice(0, 2).map((word) => word[0]?.toUpperCase() ?? '');
  return letters.join('') || name.slice(0, 2).toUpperCase();
}

/**
 * Ile dni minęło od terminu. Liczymy po dacie kalendarzowej, nie po godzinach:
 * „wczoraj o 23:00” i „wczoraj o 1:00” to dla terminu płatności ten sam jeden dzień.
 */
export function daysLate(dueIso: string | null | undefined, today = new Date()): number {
  if (!dueIso) return 0;
  const match = dueIso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return 0;
  const due = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.max(0, Math.round((now - due) / 86_400_000));
}
